import type { LogicNodeInstanceDto } from '@/schemas/logic';
import { SnapshotBlock, SnapshotBlockBuilder, SnapshotLimit } from './SnapshotBlocks';
import { validateStateValue, type StateValueBudget } from './StateValue';
import {
  hasOwnedNativeRecords,
  type GraphRecordSource,
  type NativeNodeRecord,
} from './GraphRecordSource';

const ROW_BYTES = 28;
const MAX_ROWS = 512;
const MAX_BYTES = 256 * 1024;
// Flags: output, state present, state undefined, runtime key present, key undefined.
const OUTPUT = 1,
  STATE = 2,
  UNDEFINED = 4,
  KEY = 8,
  KEY_UNDEFINED = 16;
const MAX_NODES = 1_000_000;
const NODE_FIELDS = new Set(['id', 'type', 'state', 'output', 'runtimeKey']);
const GENERATED_ID =
  /^(r:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:)([1-9][0-9]{0,9})$/;
type Segment =
  | { block: SnapshotBlock; count: number; layout?: 'native-id-v2' }
  | { overflow: readonly LogicNodeInstanceDto[] };
export type NodeSegmentPacket =
  | { root: number; count: number; layout?: 'native-id-v2' }
  | { overflow: LogicNodeInstanceDto[] };

function decimalUnits(value: number): number {
  return value < 10
    ? 1
    : value < 100
      ? 2
      : value < 1000
        ? 3
        : value < 10000
          ? 4
          : value < 100000
            ? 5
            : value < 1000000
              ? 6
              : value < 10000000
                ? 7
                : value < 100000000
                  ? 8
                  : value < 1000000000
                    ? 9
                    : 10;
}

/** Lossless compression after validation, never evidence of runtime ownership. */
function compactIdentity(node: LogicNodeInstanceDto): NativeNodeRecord {
  const match = GENERATED_ID.exec(node.id);
  if (!match) return node;
  const id = Number(match[2]);
  if (id > 0xffffffff || match[1] + id !== node.id) return node;
  const { id: _original, ...fields } = node;
  return { id, idPrefix: match[1], ...fields };
}

/** Opaque spelling is preserved, including length-prefixed UTF16 instance paths. */
function splitKey(value: string): readonly [string, string] {
  if (!value.startsWith('chip:')) return ['', value];
  const colon = value.indexOf(':', 5);
  const digits = value.slice(5, colon);
  if (colon < 0 || !/^\d+$/.test(digits)) return ['', value];
  const length = Number(digits),
    end = colon + 1 + length;
  return Number.isSafeInteger(length) && length > 0 && end <= value.length
    ? [value.slice(0, end), value.slice(end)]
    : ['', value];
}

/** Only the common, exact record shape is compacted. Exceptional records retain
 * their already cloned state; no JSON normalization and no second live read. */
function compactable(node: NativeNodeRecord): boolean {
  return (
    (typeof node.id === 'string'
      ? node.id.length * 2 + ROW_BYTES <= MAX_BYTES
      : Number.isSafeInteger(node.id) &&
        node.id > 0 &&
        node.id <= 0xffffffff &&
        'idPrefix' in node &&
        typeof node.idPrefix === 'string' &&
        (node.idPrefix.length + decimalUnits(node.id)) * 2 + ROW_BYTES <= MAX_BYTES) &&
    typeof node.type === 'string' &&
    typeof node.output === 'boolean' &&
    (node.state === null || node.state === undefined) &&
    (node.runtimeKey === undefined || typeof node.runtimeKey === 'string') &&
    node.type.length <= MAX_BYTES / 2 &&
    (node.runtimeKey?.length ?? 0) <= MAX_BYTES / 2
  );
}

function dto(node: NativeNodeRecord): LogicNodeInstanceDto {
  if (!('idPrefix' in node)) return node;
  const { idPrefix, id, ...fields } = node;
  return { id: idPrefix + id, ...fields };
}

function dense(value: unknown, max: number): asserts value is unknown[] {
  if (
    !Array.isArray(value) ||
    value.length > max ||
    Object.keys(value).length !== value.length ||
    !value.every((_, i) => Object.hasOwn(value, i))
  )
    throw new TypeError('Invalid snapshot array');
}

function validateNode(value: unknown): asserts value is LogicNodeInstanceDto {
  if (
    !value ||
    typeof value !== 'object' ||
    Array.isArray(value) ||
    (Object.getPrototypeOf(value) !== Object.prototype &&
      Object.getPrototypeOf(value) !== null)
  )
    throw new TypeError('Invalid snapshot node record');
  const node = value as LogicNodeInstanceDto;
  if (
    typeof node.id !== 'string' ||
    typeof node.type !== 'string' ||
    typeof node.output !== 'boolean' ||
    (node.runtimeKey !== undefined && typeof node.runtimeKey !== 'string') ||
    Object.keys(node).some((key) => !NODE_FIELDS.has(key))
  )
    throw new TypeError('Invalid snapshot node fields');
}

function assertNoSharedState(value: unknown, seen = new Set<object>()): void {
  if (!value || typeof value !== 'object' || seen.has(value)) return;
  seen.add(value);
  if (
    (typeof SharedArrayBuffer !== 'undefined' && value instanceof SharedArrayBuffer) ||
    (ArrayBuffer.isView(value) &&
      typeof SharedArrayBuffer !== 'undefined' &&
      value.buffer instanceof SharedArrayBuffer)
  )
    throw new TypeError('Shared mutable snapshot state is unsupported');
  if (value instanceof Map)
    for (const [key, item] of value) {
      assertNoSharedState(key, seen);
      assertNoSharedState(item, seen);
    }
  else if (value instanceof Set)
    for (const item of value) assertNoSharedState(item, seen);
  else for (const item of Object.values(value)) assertNoSharedState(item, seen);
}

/** Private immutable ownership; every reader produces fresh records. No array cache. */
export class OwnedNodeRecords {
  readonly count: number;
  #segments: readonly Segment[];
  private constructor(segments: Segment[]) {
    this.#segments = segments;
    this.count = segments.reduce(
      (n, s) => n + ('block' in s ? s.count : s.overflow.length),
      0,
    );
    Object.freeze(this);
  }

  /** Structured-clone provenance matches the save worker boundary. For native
   * nodes the source has already performed its customary deepClone(state). */
  static capture(
    source: Iterable<LogicNodeInstanceDto>,
    builder: SnapshotBlockBuilder,
  ): OwnedNodeRecords {
    return OwnedNodeRecords.captureRecords(source, builder, false);
  }

  static captureSource(
    source: GraphRecordSource,
    builder: SnapshotBlockBuilder,
  ): OwnedNodeRecords {
    const owned = hasOwnedNativeRecords(source);
    return OwnedNodeRecords.captureRecords(
      owned && source.nativeNodes ? source.nativeNodes() : source.nodes(),
      builder,
      owned,
    );
  }

  private static captureRecords(
    source: Iterable<NativeNodeRecord>,
    builder: SnapshotBlockBuilder,
    ownsNativeRecords: boolean,
  ): OwnedNodeRecords {
    const segments: Segment[] = [];
    let rows: NativeNodeRecord[] = [],
      bytes = 0,
      disabled = false;
    const appendOverflow = (nodes: LogicNodeInstanceDto[]) => {
      const previous = segments[segments.length - 1];
      if (
        previous &&
        'overflow' in previous &&
        previous.overflow.length + nodes.length <= MAX_ROWS
      )
        (previous.overflow as LogicNodeInstanceDto[]).push(...nodes);
      else segments.push({ overflow: nodes });
    };
    const flush = () => {
      if (!rows.length) return;
      const pending = rows;
      rows = [];
      const native = typeof pending[0].id === 'number';
      const buffer = new ArrayBuffer(bytes),
        view = new DataView(buffer);
      bytes = 0;
      const texts: string[] = [],
        indexes = new Map<string, number>();
      const intern = (value: string) => {
        let index = indexes.get(value);
        if (index === undefined) {
          index = texts.length;
          indexes.set(value, index);
          texts.push(value);
        }
        return index;
      };
      let textOffset = pending.length * ROW_BYTES;
      for (let i = 0; i < pending.length; i++) {
        const node = pending[i],
          offset = i * ROW_BYTES;
        let flags = node.output ? OUTPUT : 0;
        if (Object.hasOwn(node, 'state')) flags |= STATE;
        if (node.state === undefined) flags |= UNDEFINED;
        if (Object.hasOwn(node, 'runtimeKey')) flags |= KEY;
        if (node.runtimeKey === undefined) flags |= KEY_UNDEFINED;
        const [prefix, suffix] = splitKey(node.runtimeKey ?? '');
        [
          native ? (node.id as number) : textOffset,
          'idPrefix' in node ? intern(node.idPrefix) : node.id.length,
          intern(node.type),
          intern(prefix),
          intern(suffix),
          flags,
          0,
        ].forEach((word, column) => view.setUint32(offset + column * 4, word, true));
        if (typeof node.id === 'string') {
          for (let j = 0; j < node.id.length; j++)
            view.setUint16(textOffset + j * 2, node.id.charCodeAt(j), true);
          textOffset += node.id.length * 2;
        }
      }
      try {
        segments.push({
          block: builder.addRoot(buffer, texts),
          count: pending.length,
          ...(native ? { layout: 'native-id-v2' as const } : {}),
        });
      } catch (error) {
        if (!(error instanceof SnapshotLimit)) throw error;
        // Keep this already captured batch. Never recapture the live graph.
        disabled = true;
        appendOverflow(pending.map(dto));
      }
    };
    let count = 0;
    for (const input of source) {
      if (++count > MAX_NODES) throw new SnapshotLimit('Too many snapshot nodes');
      // Native common records contain only primitive fields and are freshly
      // emitted by liveGraphRecords. A second structured clone adds per-node
      // main-thread work without adding ownership. Exceptional state and public
      // iterable inputs still cross the usual clone/validation boundary.
      const ownedCommon = ownsNativeRecords && compactable(input);
      let node = ownedCommon
        ? input
        : structuredClone(ownsNativeRecords ? dto(input) : input);
      if (!ownedCommon) validateNode(node);
      if (typeof node.id === 'string' && compactable(node))
        node = compactIdentity(node as LogicNodeInstanceDto);
      if (node.state && typeof node.state === 'object') assertNoSharedState(node.state);
      if (disabled || !compactable(node)) {
        flush();
        appendOverflow([dto(node)]);
        continue;
      }
      const size = ROW_BYTES + (typeof node.id === 'string' ? node.id.length * 2 : 0);
      if (
        rows.length === MAX_ROWS ||
        bytes + size > MAX_BYTES ||
        (rows.length && typeof rows[0].id !== typeof node.id)
      )
        flush();
      if (disabled) {
        appendOverflow([dto(node)]);
        continue;
      }
      rows.push(node);
      bytes += size;
    }
    flush();
    return new OwnedNodeRecords(segments);
  }

  *nodes(): Generator<LogicNodeInstanceDto> {
    for (const segment of this.#segments) {
      if ('overflow' in segment) {
        for (const node of segment.overflow) yield structuredClone(node);
        continue;
      }
      const { block, count } = segment;
      // Column bounds do not bound expanded atom text. Keep a small cursor-local
      // cache; one oversized atom is decoded on demand, never all dependencies.
      const atoms = new Map<number, string>();
      let cachedUnits = 0;
      const text = (index: number) => {
        const cached = atoms.get(index);
        if (cached !== undefined) return cached;
        const value = block.dependencies[index].readText();
        if (cachedUnits + value.length <= 65536) {
          atoms.set(index, value);
          cachedUnits += value.length;
        }
        return value;
      };
      for (let i = 0; i < count; i++) {
        const offset = i * ROW_BYTES,
          flags = block.readUint32(offset + 20);
        const node = {
          id:
            segment.layout === 'native-id-v2'
              ? text(block.readUint32(offset + 4)) + block.readUint32(offset)
              : block.readTextRange(
                  block.readUint32(offset),
                  block.readUint32(offset + 4),
                ),
          type: text(block.readUint32(offset + 8)),
          output: !!(flags & OUTPUT),
        } as LogicNodeInstanceDto;
        if (flags & STATE) node.state = flags & UNDEFINED ? undefined : null;
        if (flags & KEY)
          node.runtimeKey =
            flags & KEY_UNDEFINED
              ? undefined
              : text(block.readUint32(offset + 12)) + text(block.readUint32(offset + 16));
        yield node;
      }
    }
  }

  /** Packet records are cloned at the transport boundary, never exposed aliases. */
  blockRoots(): readonly SnapshotBlock[] {
    return this.#segments.flatMap((segment) =>
      'block' in segment ? [segment.block] : [],
    );
  }
  validateStateBudget(budget: StateValueBudget): void {
    for (const segment of this.#segments)
      if ('overflow' in segment)
        for (const node of segment.overflow) validateStateValue(node, budget);
  }

  /** Expanded identity size is checked without decoding or retaining strings. */
  get identityCodeUnits(): number {
    let units = 0;
    for (const segment of this.#segments) {
      if ('overflow' in segment) {
        for (const node of segment.overflow)
          units += node.id.length + node.type.length + (node.runtimeKey?.length ?? 0);
        continue;
      }
      const { block, count } = segment;
      for (let i = 0; i < count; i++) {
        const row = i * ROW_BYTES;
        units +=
          segment.layout === 'native-id-v2'
            ? block.dependencies[block.readUint32(row + 4)].byteLength / 2 +
              decimalUnits(block.readUint32(row))
            : block.readUint32(row + 4);
        for (const column of [8, 12, 16])
          units += block.dependencies[block.readUint32(row + column)].byteLength / 2;
      }
    }
    return units;
  }

  /** A true result proves equality without expanding identities. A false result
   * requires semantic comparison (different packing can encode equal records). */
  *sameColumns(other: OwnedNodeRecords): Generator<void, boolean> {
    if (this === other) return true;
    if (this.count !== other.count || this.#segments.length !== other.#segments.length)
      return false;
    const compared = new WeakMap<SnapshotBlock, WeakSet<SnapshotBlock>>();
    function* same(a: SnapshotBlock, b: SnapshotBlock): Generator<void, boolean> {
      if (compared.get(a)?.has(b)) return true;
      if (!(yield* a.sameBytes(b))) return false;
      let matches = compared.get(a);
      if (!matches) {
        matches = new WeakSet();
        compared.set(a, matches);
      }
      matches.add(b);
      return true;
    }
    for (let i = 0; i < this.#segments.length; i++) {
      const a = this.#segments[i],
        b = other.#segments[i];
      if (
        !('block' in a) ||
        !('block' in b) ||
        a.count !== b.count ||
        a.layout !== b.layout ||
        a.block.dependencies.length !== b.block.dependencies.length
      )
        return false;
      if (!(yield* same(a.block, b.block))) return false;
      for (let j = 0; j < a.block.dependencies.length; j++)
        if (!(yield* same(a.block.dependencies[j], b.block.dependencies[j])))
          return false;
      yield;
    }
    return true;
  }

  packet(rootIndexes: ReadonlyMap<SnapshotBlock, number>): NodeSegmentPacket[] {
    return this.#segments.map((s) => {
      if ('overflow' in s)
        return { overflow: s.overflow.map((node) => structuredClone(node)) };
      const root = rootIndexes.get(s.block);
      if (root === undefined) throw new Error('Node block is outside capture');
      return { root, count: s.count, ...(s.layout ? { layout: s.layout } : {}) };
    });
  }

  static receive(
    packets: readonly NodeSegmentPacket[],
    roots: readonly SnapshotBlock[],
  ): OwnedNodeRecords {
    dense(packets, MAX_NODES);
    const usedRoots = new Set<number>();
    let total = 0;
    const segments: Segment[] = packets.map((packet) => {
      if (!packet || typeof packet !== 'object')
        throw new TypeError('Invalid node segment');
      if ('overflow' in packet) {
        if (Object.keys(packet).length !== 1)
          throw new TypeError('Invalid node segment variant');
        dense(packet.overflow, MAX_ROWS);
        if (!packet.overflow.length || (total += packet.overflow.length) > MAX_NODES)
          throw new SnapshotLimit('Invalid node count');
        return {
          overflow: packet.overflow.map((node) => {
            const owned = structuredClone(node);
            validateNode(owned);
            assertNoSharedState(owned);
            return owned;
          }),
        };
      }
      const native = packet.layout === 'native-id-v2';
      if (
        (Object.hasOwn(packet, 'layout') && !native) ||
        Object.keys(packet).length !== (native ? 3 : 2) ||
        usedRoots.has(packet.root)
      )
        throw new TypeError('Duplicate or invalid node segment');
      if (
        !Number.isSafeInteger(packet.root) ||
        packet.root < 0 ||
        packet.root >= roots.length ||
        !Number.isSafeInteger(packet.count) ||
        packet.count < 1 ||
        packet.count > MAX_ROWS
      )
        throw new TypeError('Invalid node segment');
      usedRoots.add(packet.root);
      if ((total += packet.count) > MAX_NODES)
        throw new SnapshotLimit('Too many snapshot nodes');
      const block = roots[packet.root],
        count = packet.count;
      if (block.byteLength < count * ROW_BYTES)
        throw new TypeError('Truncated node block');
      if (
        block.dependencies.length > count * (native ? 4 : 3) ||
        block.dependencies.some((atom) => atom.byteLength % 2 || atom.dependencies.length)
      )
        throw new TypeError('Invalid node dependencies');
      const used = new Set<number>();
      let end = count * ROW_BYTES;
      for (let i = 0; i < count; i++) {
        const offset = i * ROW_BYTES,
          start = block.readUint32(offset),
          length = block.readUint32(offset + 4);
        if (
          (native
            ? start === 0
            : start !== end || start + length * 2 > block.byteLength) ||
          block.readUint32(offset + 20) > 31 ||
          block.readUint32(offset + 24)
        )
          throw new TypeError('Invalid node columns');
        if (!native) end += length * 2;
        for (const column of native ? [4, 8, 12, 16] : [8, 12, 16]) {
          const index = block.readUint32(offset + column),
            atom = block.dependencies[index];
          if (!atom || atom.byteLength % 2 || atom.dependencies.length)
            throw new TypeError('Invalid node atom');
          used.add(index);
        }
        if (
          native &&
          block.dependencies[length].byteLength + decimalUnits(start) * 2 + ROW_BYTES >
            MAX_BYTES
        )
          throw new TypeError('Oversized native node identity');
        const flags = block.readUint32(offset + 20);
        const prefix = block.dependencies[block.readUint32(offset + 12)],
          suffix = block.dependencies[block.readUint32(offset + 16)];
        if (
          (!(flags & STATE) && !(flags & UNDEFINED)) ||
          (!(flags & KEY) && !(flags & KEY_UNDEFINED)) ||
          (flags & KEY_UNDEFINED && (prefix.byteLength || suffix.byteLength)) ||
          prefix.byteLength + suffix.byteLength > MAX_BYTES
        )
          throw new TypeError('Invalid optional node columns');
      }
      if (end !== block.byteLength) throw new TypeError('Trailing node bytes');
      if (used.size !== block.dependencies.length)
        throw new TypeError('Unused node dependencies');
      return { block, count, ...(native ? { layout: 'native-id-v2' as const } : {}) };
    });
    return new OwnedNodeRecords(segments);
  }
}
